/**
 * DraggableMockup Component (Stufe 5.3)
 *
 * Wrapper around MockupRenderer that adds drag-to-reposition and
 * scroll-to-scale interactions for per-product design placement.
 *
 * The component:
 * - Renders the product mockup at a large size (fitting available space)
 * - Shows a dashed PrintArea boundary for orientation
 * - Handles pointer events to compute dxPx/dyPx overrides
 * - Handles wheel events to compute scale overrides
 * - Shows the placement the carry-over rule (or a custom placement) yields
 *
 * IMPORTANT: This component does NOT modify the master canvas.
 * It only adjusts the per-product placement overrides (Level 2).
 */

import {
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { default as MockupRenderer } from './MockupRenderer';
import type { UIProductMockupTemplate } from '@/lib/product-transformer';
import type { AdaptedPlacement, RelativeDesignState } from '@designdeck/shared';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Relative width limits to avoid invalid placements */
const MIN_REL_WIDTH = 0.02;
const MAX_REL_WIDTH = 2;
/** Relative width delta per scroll tick */
const REL_WIDTH_STEP = 0.02;

// =============================================================================
// TYPES
// =============================================================================

interface DraggableMockupProps {
  /** Mockup template for the product */
  template: UIProductMockupTemplate;
  /** Product background color hex */
  backgroundColor: string;
  /** Whether color is heathered */
  isHeathered?: boolean;
  /** Embroidered product: the shading base sits below the design */
  isEmbroidery?: boolean;
  /** Final adapted placement (with overrides already applied) */
  placement: AdaptedPlacement | null;
  /** Crop image source for adapted rendering */
  designCropUrl: string | null;
  /** Current editable placement (center/width model) */
  placementState: RelativeDesignState;
  /** Callback when placement changes (live, during drag) */
  onPlacementChange: (placement: RelativeDesignState) => void;
}

// =============================================================================
// DRAG STATE
// =============================================================================

interface DragState {
  startX: number;
  startY: number;
  startPlacement: RelativeDesignState;
}

// =============================================================================
// HELPER: Fit mockup into container while preserving aspect ratio
// =============================================================================

function fitToContainer(
  mockupW: number,
  mockupH: number,
  containerW: number,
  containerH: number
): { width: number; height: number } {
  if (containerW <= 0 || containerH <= 0 || mockupW <= 0 || mockupH <= 0) {
    return { width: 0, height: 0 };
  }
  const scaleX = containerW / mockupW;
  const scaleY = containerH / mockupH;
  const fitScale = Math.min(scaleX, scaleY);
  return {
    width: Math.round(mockupW * fitScale),
    height: Math.round(mockupH * fitScale),
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

export function DraggableMockup({
  template,
  backgroundColor,
  isHeathered = false,
  isEmbroidery = false,
  placement,
  designCropUrl,
  placementState,
  onPlacementChange,
}: DraggableMockupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragLayerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  // Refs for current values (avoids stale closures in imperative event handlers)
  const placementStateRef = useRef(placementState);
  placementStateRef.current = placementState;
  const onPlacementChangeRef = useRef(onPlacementChange);
  onPlacementChangeRef.current = onPlacementChange;

  // Measure container for responsive sizing
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Compute mockup display size (fit into container with padding)
  const mockupWidth = template.mockupWidthPx;
  const mockupHeight = template.mockupHeightPx;
  const rect = template.printAreaRect;
  const mockupSize = fitToContainer(
    mockupWidth,
    mockupHeight,
    containerSize.width,
    containerSize.height
  );

  // Display scale: how many screen pixels = 1 mockup pixel
  const displayScale =
    mockupSize.width > 0 ? mockupSize.width / mockupWidth : 1;

  // PrintArea relative position on the mockup (for the dashed boundary)
  const printAreaRelLeft = mockupWidth > 0 ? rect.x / mockupWidth : 0;
  const printAreaRelTop = mockupHeight > 0 ? rect.y / mockupHeight : 0;
  const printAreaRelWidth = mockupWidth > 0 ? rect.width / mockupWidth : 0;
  const printAreaRelHeight = mockupHeight > 0 ? rect.height / mockupHeight : 0;

  // ─────────────────────────────────────────────────────────────────
  // DRAG HANDLERS (React synthetic events — pointer events are fine)
  // ─────────────────────────────────────────────────────────────────

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // setPointerCapture ensures we receive pointer events even outside the element.
      // Guard: not available in jsdom test environment.
      const target = e.target as HTMLElement;
      if (typeof target.setPointerCapture === 'function') {
        target.setPointerCapture(e.pointerId);
      }
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPlacement: { ...placementStateRef.current },
      };
    },
    [] // stable — reads from overrideRef at call time
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;

      const dxScreen = e.clientX - dragRef.current.startX;
      const dyScreen = e.clientY - dragRef.current.startY;

      // Convert screen pixels -> print-area relative deltas.
      // screen -> mockup px via displayScale, then normalize by print-area size.
      const dxPrintAreaPx = dxScreen / displayScale;
      const dyPrintAreaPx = dyScreen / displayScale;
      const deltaRelX = rect.width > 0 ? dxPrintAreaPx / rect.width : 0;
      const deltaRelY = rect.height > 0 ? dyPrintAreaPx / rect.height : 0;

      onPlacementChangeRef.current({
        ...dragRef.current.startPlacement,
        relCenterX: dragRef.current.startPlacement.relCenterX + deltaRelX,
        relCenterY: dragRef.current.startPlacement.relCenterY + deltaRelY,
      });
    },
    [displayScale, rect.height, rect.width]
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // SCROLL-TO-SCALE HANDLER (imperative, non-passive)
  //
  // React 17+ registers wheel listeners as passive by default.
  // A passive listener cannot call preventDefault(), so the page
  // would scroll AND scale simultaneously. We use an imperative
  // addEventListener with { passive: false } to fix this.
  // ─────────────────────────────────────────────────────────────────

  // Re-attach when the drag layer element appears (after container measurement).
  // containerSize dependency ensures we re-run when the element becomes available.
  useEffect(() => {
    const el = dragLayerRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -REL_WIDTH_STEP : REL_WIDTH_STEP;
      const nextRelWidth = Math.max(
        MIN_REL_WIDTH,
        Math.min(MAX_REL_WIDTH, placementStateRef.current.relWidth + delta)
      );

      onPlacementChangeRef.current({
        ...placementStateRef.current,
        relWidth: nextRelWidth,
      });
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [containerSize]); // Re-attach when container size changes (drag layer appears)

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full flex items-center justify-center"
    >
      {mockupSize.width > 0 && mockupSize.height > 0 && (
        <div
          style={{ width: mockupSize.width, height: mockupSize.height }}
          className="relative"
        >
          {/* Product Mockup with adapted design */}
          <MockupRenderer
            template={template}
            backgroundColor={backgroundColor}
            adaptedPlacement={placement}
            designCropUrl={designCropUrl}
            isHeathered={isHeathered}
            isEmbroidery={isEmbroidery}
            width={mockupSize.width}
            height={mockupSize.height}
            simplified={false}
            lazyLoad={false}
            priority="high"
          />

          {/* Dashed PrintArea boundary (orientation guide) */}
          <div
            className="absolute border-2 border-dashed border-white/40 rounded pointer-events-none z-10"
            style={{
              left: `${printAreaRelLeft * 100}%`,
              top: `${printAreaRelTop * 100}%`,
              width: `${printAreaRelWidth * 100}%`,
              height: `${printAreaRelHeight * 100}%`,
            }}
          />

          {/* Invisible drag layer over PrintArea */}
          <div
            ref={dragLayerRef}
            className="absolute cursor-grab active:cursor-grabbing z-20"
            style={{
              left: `${printAreaRelLeft * 100}%`,
              top: `${printAreaRelTop * 100}%`,
              width: `${printAreaRelWidth * 100}%`,
              height: `${printAreaRelHeight * 100}%`,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            // NOTE: wheel is handled imperatively via useEffect (non-passive)
          />
        </div>
      )}
    </div>
  );
}

export default DraggableMockup;
